import { unwrap } from '@hive/api';
import type { PillTone } from '@hive/ui';
import { downloadFrom } from './reports';
import type { useSession } from './session';

/** Matriz de SLA (prova mensal) e causas com Pareto: tipos e chamadas da API do núcleo. */

type Sess = ReturnType<typeof useSession>;
type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';
type Raw = Record<Method, (path: string, opts?: unknown) => Promise<never>>;
const raw = (s: Sess) => s.api as unknown as Raw;

export async function call<T>(s: Sess, method: Method, path: string, opts?: { query?: Record<string, string | number | boolean | undefined>; body?: unknown }): Promise<T> {
  const query = opts?.query ? Object.fromEntries(Object.entries(opts.query).filter(([, v]) => v !== undefined && v !== '')) : undefined;
  return (await unwrap(raw(s)[method](path, { ...(query ? { params: { query } } : {}), ...(opts?.body !== undefined ? { body: opts.body } : {}) }))) as T;
}

export async function download(s: Sess, path: string, fallback: string) {
  const res = (await raw(s).GET(path, { parseAs: 'blob' })) as unknown as { data?: unknown; response: Response };
  await downloadFrom(res, fallback);
}

export type Band = 'ok' | 'warn' | 'crit' | 'maint' | 'none';
export const BANDS: Band[] = ['ok', 'warn', 'crit', 'maint', 'none'];
export const bandTone = (b?: string): PillTone => (b === 'ok' ? 'ok' : b === 'warn' ? 'warn' : b === 'crit' ? 'crit' : b === 'maint' ? 'info' : 'neutral');
/** Cor da célula por token (o acento é a marca do cliente; as faixas usam os tons de estado). */
export const bandColor = (b?: string) => (b === 'ok' ? 'var(--ok)' : b === 'warn' ? 'var(--warn)' : b === 'crit' ? 'var(--crit)' : b === 'maint' ? 'var(--info)' : 'var(--line)');

export type SlaDay = { day: number; pct: number | null; monitoredMin: number; downMin: number; maintMin: number; incidents: number; band: Band };
export type SlaTotal = { pct: number | null; monitoredMin: number; downMin: number; maintMin: number; incidents: number; mttrMin: number | null; met: boolean | null; budgetMin: number; budgetLeftMin: number; band: Band };
export type SlaRow = { id: string; name: string; kind: string; targetPct: number; criticality: string | null; site: string | null; days: SlaDay[]; month: SlaTotal };
export type SlaMatrix = {
  month: string;
  timezone: string;
  kind: string;
  daysInMonth: number;
  today: number | null;
  warnMargin: number;
  rows: SlaRow[];
  summary: { subjects: number; met: number; breached: number; noData: number; avgPct: number | null; downMin: number; maintMin: number; incidents: number };
  bands: Record<Band, number>;
  worst: { id: string; name: string; pct: number | null; targetPct: number; downMin: number; gap: number }[];
  worstDay: { day: number; downMin: number; subjects: number; avgPct: number | null } | null;
  pareto: { id: string; name: string; downMin: number; pct: number; cumPct: number }[];
  concentration: { pct: number; subjects: number; of: number };
};
export type ClosedMeta = { id: string; month: string; version: number; timezone: string; hash: string; prevHash: string | null; supersedes: string | null; reason: string; closedBy: string; closedAt: string };
export type MatrixResponse = { matrix: SlaMatrix; closed: ClosedMeta | null; filtered?: boolean };
export type ReportItem = ClosedMeta & { latest: boolean; summary: Record<'services' | 'sites' | 'assets', { subjects: number; met: number; breached: number; avgPct: number | null; downMin: number; incidents: number }> };
export type SlaSettings = { timezone: string; siteTargetPct: number; assetTargetPct: number; autoClose: boolean; closeDay: number; emails: string[]; connections: string[]; format: 'pdf' | 'xlsx' | 'html'; lang: 'en' | 'pt-BR' | 'es'; updatedBy: string | null; updatedAt: string | null };
export type Verification = { id: string; hash: string; computed: string; ok: boolean; chainOk: boolean; previousId: string | null };
export type Delivery = { id: number; at: string; manual: boolean; ok: boolean; detail: { to: string; ok: boolean; detail: string }[] };

export const slaApi = {
  matrix: (s: Sess, month: string, kind: string, live = false) => call<MatrixResponse>(s, 'GET', '/api/v1/sla-matrix', { query: { month, kind, live: live || undefined } }),
  reports: (s: Sess) => call<{ items: ReportItem[]; nextCursor: string | null }>(s, 'GET', '/api/v1/sla-reports', { query: { limit: 200 } }),
  close: (s: Sess, month: string, reason?: string) => call<ReportItem>(s, 'POST', '/api/v1/sla-reports', { body: { month, reason } }),
  verify: (s: Sess, id: string) => call<Verification>(s, 'GET', `/api/v1/sla-reports/${encodeURIComponent(id)}/verify`),
  send: (s: Sess, id: string) => call<Delivery>(s, 'POST', `/api/v1/sla-reports/${encodeURIComponent(id)}/send`),
  deliveries: (s: Sess, id: string) => call<Delivery[]>(s, 'GET', `/api/v1/sla-reports/${encodeURIComponent(id)}/deliveries`),
  export: (s: Sess, id: string, format: 'pdf' | 'xlsx' | 'html', lang: string, month: string) =>
    download(s, `/api/v1/sla-reports/${encodeURIComponent(id)}/export?format=${format}&lang=${encodeURIComponent(lang)}`, `sla-${month}.${format}`),
  settings: (s: Sess) => call<SlaSettings>(s, 'GET', '/api/v1/sla-reports/settings'),
  saveSettings: (s: Sess, body: Partial<SlaSettings>) => call<SlaSettings>(s, 'PUT', '/api/v1/sla-reports/settings', { body }),
};

export type Cause = { key: string; name: string | null; label: string; preventable: boolean; enabled: boolean; builtin: boolean; position: number; uses: number };
export type ParetoItem = { key: string; label: string; preventable: boolean; count: number; minutes: number; value: number; pct: number; cumPct: number };
export type Facet = { value: string; label: string; count: number };
export type Pareto = {
  from: string;
  to: string;
  measure: 'count' | 'minutes';
  total: number;
  classified: number;
  unclassified: number;
  suggested: number;
  preventablePct: number;
  headline: { pct: number; causes: number; of: number };
  items: ParetoItem[];
  facets: Partial<Record<'service' | 'site' | 'kind' | 'severity', Facet[]>>;
};
export type Incident = {
  id: string;
  title: string;
  assetId: string | null;
  asset: string | null;
  assetKind: string | null;
  site: string | null;
  severity: string;
  state: string;
  openedAt: string;
  resolvedAt: string;
  durationMin: number;
  causeKey: string | null;
  causeNote: string | null;
  causeBy: string | null;
  causeAt: string | null;
  suggested: string | null;
  suggestedReason: string | null;
};
export type CauseFilter = { days: number; service?: string; site?: string; kind?: string; severity?: string };
export type Summary = { days: number; total: number; classified: number; unclassified: number; preventablePct: number; headline: Pareto['headline']; top: ParetoItem[] };

export const rcaApi = {
  taxonomy: (s: Sess) => call<Cause[]>(s, 'GET', '/api/v1/root-causes/taxonomy'),
  create: (s: Sess, body: { name: string; preventable: boolean }) => call<Cause>(s, 'POST', '/api/v1/root-causes/taxonomy', { body }),
  update: (s: Sess, key: string, body: { name?: string | null; preventable?: boolean; enabled?: boolean }) =>
    call<Cause>(s, 'PUT', `/api/v1/root-causes/taxonomy/${encodeURIComponent(key)}`, { body }),
  remove: (s: Sess, key: string) => call<void>(s, 'DELETE', `/api/v1/root-causes/taxonomy/${encodeURIComponent(key)}`),
  pareto: (s: Sess, f: CauseFilter, measure: 'count' | 'minutes') => call<Pareto>(s, 'GET', '/api/v1/root-causes/pareto', { query: { ...f, measure } }),
  incidents: (s: Sess, f: CauseFilter & { cause?: string; state?: string; cursor?: string }) =>
    call<{ items: Incident[]; nextCursor: string | null }>(s, 'GET', '/api/v1/root-causes/incidents', { query: { ...f, limit: 200 } }),
  set: (s: Sess, alertId: string, cause: string, note?: string) => call<unknown>(s, 'PUT', `/api/v1/root-causes/incidents/${encodeURIComponent(alertId)}`, { body: { cause, note } }),
  suggestion: (s: Sess, alertId: string) =>
    call<{ cause: string | null; reason: string | null }>(s, 'GET', `/api/v1/root-causes/incidents/${encodeURIComponent(alertId)}/suggestion`),
  accept: (s: Sess, alertIds: string[]) => call<{ applied: number; skipped: number }>(s, 'POST', '/api/v1/root-causes/incidents/accept-suggestions', { body: { alertIds } }),
  summary: (s: Sess, days = 30) => call<Summary>(s, 'GET', '/api/v1/root-causes/summary', { query: { days } }),
};

/** "99,95%" com até três casas e sem zeros à direita; null → "—". */
export function pctText(v: number | null | undefined, fmtNumber: (n: number) => string): string {
  return v == null ? '—' : `${fmtNumber(Math.round(v * 1000) / 1000)}%`;
}

/** Meses que já podem ser fechados (os 12 anteriores ao atual), do mais novo ao mais velho. */
export function pastMonths(now = new Date(), n = 12): string[] {
  const out: string[] = [];
  for (let i = 1; i <= n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

export function currentMonth(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}
